/**
 * The line form (a slickline, braided line or e-line on the register), used
 * full-page by New Line and as the edit panel on a line's own page.
 *
 * OD is entered in mm or in (default meta.units.diameter) and breaking
 * strength in daN, kN or lbf (default meta.units.force); the API keeps each
 * as entered plus its metric copy. Nothing is converted here. The conductor
 * count is an e-line field and is only shown for one.
 *
 * Checked before sending, each with a sentence: a line number, a line type,
 * positive numbers, intervals as whole days 1–3650, conductors 1–12. Those
 * refuse the save. A blank interval is a WARNING only — the API accepts it
 * and simply does not track that test — so it is said, and Save stays live.
 *
 * A blank field is sent as null (absent), never as 0 or "". On edit only the
 * keys that changed are sent.
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { isWlElectricLine, wlBlankToNull, wlToInput } from '../../lib/wireline-slickline';
import type { WlCatalog, WlLine, WlLineWrite, WlMeta } from '../../types/wireline-slickline';
import { WL_INPUT, WL_PRIMARY, WL_SECONDARY, WlField, WlFormError, WlWarning } from './WlBits';

export interface LineFormState {
  line_number: string;
  line_type: string;
  manufacturer: string;
  serial_number: string;
  material: string;
  od: string;
  od_unit: string;
  breaking_strength: string;
  breaking_strength_unit: string;
  conductor_count: string;
  pull_test_interval_days: string;
  inspection_interval_days: string;
  notes: string;
}

export const lineFormState = (l: WlLine | null, diameterUnit: string, forceUnit: string): LineFormState => ({
  line_number: l?.lineNumber ?? '',
  line_type: l?.lineType ?? '',
  manufacturer: l?.manufacturer ?? '',
  serial_number: l?.serialNumber ?? '',
  material: l?.material ?? '',
  od: wlToInput(l?.od),
  od_unit: l?.odUnit ?? diameterUnit,
  breaking_strength: wlToInput(l?.breakingStrength),
  breaking_strength_unit: l?.breakingStrengthUnit ?? forceUnit,
  conductor_count: wlToInput(l?.conductorCount),
  pull_test_interval_days: wlToInput(l?.pullTestIntervalDays),
  inspection_interval_days: wlToInput(l?.inspectionIntervalDays),
  notes: l?.notes ?? '',
});

const POSITIVE = /^\d+(\.\d+)?$/;
const WHOLE = /^\d+$/;

/** The first thing that refuses the save, as a sentence; null when it may be sent. */
export function lineFormError(form: LineFormState): string | null {
  if (!/[A-Za-z0-9]/.test(form.line_number)) return 'Give the line a number, e.g. SL-104.';
  if (!form.line_type) return 'Pick the line type.';
  const od = form.od.trim();
  if (od !== '' && (!POSITIVE.test(od) || Number(od) <= 0)) return 'OD must be a number greater than 0.';
  const bs = form.breaking_strength.trim();
  if (bs !== '' && (!POSITIVE.test(bs) || Number(bs) <= 0)) return 'Breaking strength must be a number greater than 0.';
  const cc = form.conductor_count.trim();
  if (isWlElectricLine(form.line_type) && cc !== '' && (!WHOLE.test(cc) || Number(cc) < 1 || Number(cc) > 12)) {
    return 'Conductors must be a whole number from 1 to 12.';
  }
  for (const [k, label] of [['pull_test_interval_days', 'Pull-test interval'], ['inspection_interval_days', 'Inspection interval']] as const) {
    const v = form[k].trim();
    if (v !== '' && (!WHOLE.test(v) || Number(v) < 1 || Number(v) > 3650)) return `${label} must be a whole number of days from 1 to 3650.`;
  }
  return null;
}

/** Worth saying, never blocking: the API accepts a line with no intervals and does not track that test. */
export function lineFormWarning(form: LineFormState): string | null {
  const noPull = form.pull_test_interval_days.trim() === '';
  const noInspect = form.inspection_interval_days.trim() === '';
  if (noPull && noInspect) return 'No pull-test or inspection interval: neither will be tracked as due for this line.';
  if (noPull) return 'No pull-test interval: pull tests will not be tracked as due for this line.';
  if (noInspect) return 'No inspection interval: inspections will not be tracked as due for this line.';
  return null;
}

const numOrNull = (s: string) => (s.trim() === '' ? null : Number(s.trim()));

/** The write body: every filled field on create, only the changed ones on edit. */
export function lineWriteBody(form: LineFormState, initial: LineFormState | null): WlLineWrite {
  const body: WlLineWrite = {};
  const changed = (k: keyof LineFormState) => !initial || form[k] !== initial[k];
  if (changed('line_number')) body.line_number = form.line_number.trim();
  if (changed('line_type')) body.line_type = form.line_type;
  if (changed('manufacturer')) body.manufacturer = wlBlankToNull(form.manufacturer);
  if (changed('serial_number')) body.serial_number = wlBlankToNull(form.serial_number);
  if (changed('material')) body.material = wlBlankToNull(form.material);
  if (changed('od') || changed('od_unit')) {
    body.od = numOrNull(form.od);
    if (form.od.trim() !== '') body.od_unit = form.od_unit || null;
  }
  if (changed('breaking_strength') || changed('breaking_strength_unit')) {
    body.breaking_strength = numOrNull(form.breaking_strength);
    if (form.breaking_strength.trim() !== '') body.breaking_strength_unit = form.breaking_strength_unit || null;
  }
  // Conductors belong to an e-line: switching a line off e-line clears them.
  if (changed('conductor_count') || changed('line_type')) {
    body.conductor_count = isWlElectricLine(form.line_type) ? numOrNull(form.conductor_count) : null;
  }
  if (changed('pull_test_interval_days')) body.pull_test_interval_days = numOrNull(form.pull_test_interval_days);
  if (changed('inspection_interval_days')) body.inspection_interval_days = numOrNull(form.inspection_interval_days);
  if (changed('notes')) body.notes = wlBlankToNull(form.notes);
  if (!initial) (Object.keys(body) as (keyof WlLineWrite)[]).forEach((k) => body[k] === null && delete body[k]);
  return body;
}

export function LineForm({
  line,
  catalog,
  meta,
  pending,
  error,
  duplicateOf,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  line: WlLine | null;
  catalog: WlCatalog | null;
  meta: WlMeta | null;
  pending: boolean;
  error: string | null;
  duplicateOf?: string | null;
  submitLabel: string;
  onSubmit: (body: WlLineWrite) => void;
  onCancel: () => void;
}) {
  const diameterUnit = meta?.units?.diameter ?? '';
  const forceUnit = meta?.units?.force ?? '';
  const initial = useMemo(() => (line ? lineFormState(line, diameterUnit, forceUnit) : null), [line, diameterUnit, forceUnit]);
  const [form, setForm] = useState<LineFormState>(() => lineFormState(line, diameterUnit, forceUnit));
  const [localError, setLocalError] = useState<string | null>(null);
  const set = <K extends keyof LineFormState>(k: K, v: LineFormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const diameterUnits = catalog?.diameterUnits?.length ? catalog.diameterUnits : diameterUnit ? [diameterUnit] : [];
  const forceUnits = catalog?.forceUnits?.length ? catalog.forceUnits : forceUnit ? [forceUnit] : [];
  const warning = lineFormWarning(form);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    const problem = lineFormError(form);
    if (problem) return setLocalError(problem);
    const body = lineWriteBody(form, initial);
    if (initial && Object.keys(body).length === 0) return setLocalError('Nothing has changed.');
    onSubmit(body);
  };

  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="wl-line-form" noValidate>
      <WlFormError message={localError ?? error} />
      {!localError && error && duplicateOf && (
        <p className="text-sm" data-testid="wl-line-duplicate-link">
          <Link to={`/wireline-slickline/lines/${duplicateOf}`} className="font-semibold text-fieldiq-text underline">
            Open the existing line
          </Link>
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <WlField label="Line number" hint="Your own number for the line, e.g. SL-104.">
          <input className={WL_INPUT} maxLength={50} value={form.line_number} onChange={(e) => set('line_number', e.target.value)} aria-label="Line number" />
        </WlField>
        <WlField label="Line type">
          <select className={WL_INPUT} value={form.line_type} onChange={(e) => set('line_type', e.target.value)} aria-label="Line type">
            <option value="">Select a type…</option>
            {(catalog?.lineTypes ?? []).map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </WlField>
        <WlField label="Manufacturer">
          <input className={WL_INPUT} value={form.manufacturer} onChange={(e) => set('manufacturer', e.target.value)} aria-label="Manufacturer" />
        </WlField>
        <WlField label="Serial number">
          <input className={WL_INPUT} value={form.serial_number} onChange={(e) => set('serial_number', e.target.value)} aria-label="Serial number" />
        </WlField>
        <WlField label="Material" hint="e.g. carbon steel, 316 stainless">
          <input className={WL_INPUT} value={form.material} onChange={(e) => set('material', e.target.value)} aria-label="Material" />
        </WlField>
        <WlField label="OD" hint="Blank when not known.">
          <div className="flex gap-2">
            <input className={WL_INPUT} inputMode="decimal" value={form.od} onChange={(e) => set('od', e.target.value)} aria-label="OD" />
            <select className={`${WL_INPUT} w-24 shrink-0`} value={form.od_unit} onChange={(e) => set('od_unit', e.target.value)} aria-label="OD unit">
              {!form.od_unit && <option value="">—</option>}
              {diameterUnits.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
        </WlField>
        <WlField label="Breaking strength" hint="Blank when not known.">
          <div className="flex gap-2">
            <input className={WL_INPUT} inputMode="decimal" value={form.breaking_strength} onChange={(e) => set('breaking_strength', e.target.value)} aria-label="Breaking strength" />
            <select
              className={`${WL_INPUT} w-24 shrink-0`}
              value={form.breaking_strength_unit}
              onChange={(e) => set('breaking_strength_unit', e.target.value)}
              aria-label="Breaking strength unit"
            >
              {!form.breaking_strength_unit && <option value="">—</option>}
              {forceUnits.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
        </WlField>
        {isWlElectricLine(form.line_type) && (
          <WlField label="Conductors" hint="1 to 12.">
            <input className={WL_INPUT} inputMode="numeric" value={form.conductor_count} onChange={(e) => set('conductor_count', e.target.value)} aria-label="Conductors" />
          </WlField>
        )}
        <WlField label="Pull-test interval (days)" hint="Blank: pull tests are not tracked as due.">
          <input className={WL_INPUT} inputMode="numeric" value={form.pull_test_interval_days} onChange={(e) => set('pull_test_interval_days', e.target.value)} aria-label="Pull-test interval (days)" />
        </WlField>
        <WlField label="Inspection interval (days)" hint="Blank: inspections are not tracked as due.">
          <input className={WL_INPUT} inputMode="numeric" value={form.inspection_interval_days} onChange={(e) => set('inspection_interval_days', e.target.value)} aria-label="Inspection interval (days)" />
        </WlField>
      </div>
      <WlField label="Notes">
        <textarea className={`${WL_INPUT} h-24 py-2`} value={form.notes} onChange={(e) => set('notes', e.target.value)} aria-label="Notes" />
      </WlField>
      <WlWarning message={warning} testId="wl-line-warning" />
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={WL_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={WL_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
